import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  Button,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Table,
} from "reactstrap";
import { BaseProps } from "../../../models/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapLocationDot, faPlus, faSave, faStop } from "@fortawesome/free-solid-svg-icons";
import { IUserSuburbSelection, userSuburbSelectionSchema, create, list, update } from "../../../models/userSuburbSelection";
import { SuburbShorlistingControl } from "./shortlisting";

import { DisclaimerComponent } from "../../../components/disclaimer";
import { OrbitProgress } from "react-loading-indicators";

interface SuburbSelectionPageProps extends BaseProps {}

export const SuburbSelectionPage = (props: SuburbSelectionPageProps) => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [loading, setIsLoading] = useState<boolean>(true);
  const [userSuburbSelection, setUserSuburbSelection] = useState<IUserSuburbSelection>();
  const [userSuburbSelections, setUserSuburbSelections] = useState<IUserSuburbSelection[]>([]);
  const [errors, setErrors] = useState<IUserSuburbSelection>({});
  const [activeTab, setActiveTab] = useState<number>(0);

  useEffect(() => {
    if (props.profile?.id) {
      list(props.profile?.id).then((response) => {
        setUserSuburbSelections(response.data.userSuburbSelections);
        setIsLoading(false);
      });
    }
  }, [props.profile]);

  if (loading) {
    return (
      <div className="spinner-center">
        <OrbitProgress variant="track-disc" color={"#319dcc"} speedPlus={0} easing="linear" />
      </div>
    );
  }

  return (
    <>
      <Modal isOpen={modalIsOpen} toggle={(x) => setModalIsOpen(x != x)}>
        <ModalHeader>Add a Suburb</ModalHeader>
        <ModalBody>
          <Row>
            <Col>
              <Label for="txtName">Name</Label>
              {errors?.name && (
                <>
                  <br />
                  <span className="text-danger">{errors.name}</span>
                </>
              )}
              <Input
                id="txtName"
                value={userSuburbSelection?.name}
                onChange={(e) => {
                  e.persist();
                  setUserSuburbSelection((x) => ({ ...x, name: e.target.value }));
                }}
              ></Input>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={async () => {
              try {
                await userSuburbSelectionSchema.validateSync(userSuburbSelection, {
                  abortEarly: false,
                });
                if (userSuburbSelection?.id) {
                  await update(userSuburbSelection);
                } else {
                  await create(userSuburbSelection);
                }
                window.location.reload();
              } catch (yupErrors) {
                let errors: IUserSuburbSelection = {};
                yupErrors.inner.map((x: any) => {
                  errors[x.path] = x.message;
                });
                setErrors(errors);
              }
            }}
          >
            <FontAwesomeIcon icon={faSave} className="mr-2"></FontAwesomeIcon>
            Save
          </Button>
          <Button color="secondary" onClick={() => setModalIsOpen(false)}>
            <FontAwesomeIcon icon={faStop} className="mr-2"></FontAwesomeIcon>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
      <Helmet>
        <meta charSet="utf-8"></meta>
        <title>Suburb selection tool | Asset manager</title>
      </Helmet>
      <Container>
        <h1>
          <FontAwesomeIcon icon={faMapLocationDot} className="mr-2"></FontAwesomeIcon>Suburb selection tool
        </h1>
        <hr />
        <Button
          color="primary"
          className="mb-2"
          onClick={() => {
            setErrors({});
            let sortOrder = userSuburbSelections.length + 1;
            setUserSuburbSelection({ sortOrder, ownerId: props.profile?.id });
            setModalIsOpen(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2"></FontAwesomeIcon>Add a new Suburb
        </Button>

        <>
          <Nav tabs>
            {userSuburbSelections?.map((x, i) => {
              return (
                <NavItem key={i}>
                  <NavLink href="#" active={activeTab == i} onClick={() => setActiveTab(i)}>
                    {x.name}
                  </NavLink>
                </NavItem>
              );
            })}
          </Nav>
          <TabContent activeTab={activeTab}>
            {userSuburbSelections.length > 0 &&
              userSuburbSelections?.map((x, i) => {
                return (
                  <TabPane key={i} tabId={i} className="mt-2">
                    <SuburbShorlistingControl userSuburbSelectionId={x?.id} />
                  </TabPane>
                );
              })}
          </TabContent>
        </>
        <DisclaimerComponent />
      </Container>
    </>
  );
};
